import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeProvider";

const BASE =
  "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]";

const SIZE = {
  sm: "h-9 px-4 text-[12px]",
  md: "h-11 px-6 text-[13px]",
  lg: "h-12 px-7 text-[14px]",
  xl: "h-14 px-9 text-[14px]",
};

function stylesFor(theme, variant) {
  const radius = {
    "theme-castlewood": "rounded-[2px]",
    "theme-gmh": "rounded-[8px]",
    "theme-vela": "rounded-[10px]",
    "theme-brionj": "rounded-none",
    "theme-admin": "rounded-[8px]",
  }[theme] || "rounded-md";

  if (theme === "theme-castlewood") {
    const base = `${radius} smallcaps-wide font-display`;
    if (variant === "secondary")
      return `${base} bg-transparent text-foreground border border-[hsl(var(--brand-accent))] hover:bg-[hsl(var(--brand-tint))]`;
    if (variant === "ghost")
      return `${base} bg-transparent text-foreground underline decoration-[hsl(var(--brand-accent))] underline-offset-[8px] hover:decoration-[hsl(var(--foreground))]`;
    return `${base} bg-primary text-primary-foreground border border-primary hover:opacity-95`;
  }

  if (theme === "theme-brionj") {
    const base = `${radius} smallcaps-xwide font-ui`;
    if (variant === "secondary")
      return `${base} bg-transparent text-foreground border border-[hsl(var(--brand-accent))] hover:bg-[hsl(var(--brand-tint))]`;
    if (variant === "ghost")
      return `${base} bg-transparent text-[hsl(var(--brand-accent))] underline decoration-[hsl(var(--brand-accent))] underline-offset-[8px]`;
    return `${base} bg-[hsl(var(--brand-accent))] text-[hsl(var(--background))] border border-[hsl(var(--brand-accent))] hover:bg-[hsl(var(--background))] hover:text-[hsl(var(--brand-accent))]`;
  }

  if (theme === "theme-vela") {
    const base = `${radius} font-ui`;
    if (variant === "secondary")
      return `${base} bg-secondary text-secondary-foreground border border-border hover:bg-accent`;
    if (variant === "ghost")
      return `${base} bg-transparent text-foreground hover:bg-secondary`;
    return `${base} bg-primary text-primary-foreground hover:opacity-95`;
  }

  // gmh + admin default
  const base = `${radius} font-ui`;
  if (variant === "secondary")
    return `${base} bg-secondary text-secondary-foreground border border-border hover:bg-accent`;
  if (variant === "ghost")
    return `${base} bg-transparent text-foreground hover:bg-secondary`;
  return `${base} bg-primary text-primary-foreground hover:opacity-95`;
}

const BrandButton = React.forwardRef(
  ({ as, to, href, variant = "primary", size = "md", className, children, ...rest }, ref) => {
    const { theme } = useTheme();
    const cls = cn(BASE, SIZE[size], stylesFor(theme, variant), className);
    if (to) return (<Link ref={ref} to={to} className={cls} {...rest}>{children}</Link>);
    if (href) return (<a ref={ref} href={href} className={cls} {...rest}>{children}</a>);
    const Comp = as || "button";
    return (
      <Comp ref={ref} className={cls} {...rest}>
        {children}
      </Comp>
    );
  }
);
BrandButton.displayName = "BrandButton";

export default BrandButton;
export { BrandButton };
